import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import {
	Button,
	ButtonProps,
	ButtonVariant,
} from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';

import { Divider } from './mobile-nav/icons/divider';
import { GLOBAL_NAV_HEIGHT } from './nav-global';
import GlobalNavLinksContainer, { GlobalNavLinkContentProps } from './nav-links-container';

export type CompoundSubNavProps = {
	compoundSubNavTitle: string;
	compoundSubNavLinksProps: GlobalNavLinkContentProps[];
	isShowCta?: boolean;
	ctaButtonText?: string;
	ctaButtonVariant?: ButtonVariant;
	ctaUrl?: string;
	productKey?: ProductKeyTypes;
	optionalClassName?: string;
	compoundSubNavTitleHref?: string;
};

const compoundSubNavPadding = token('space.100');

const linkableCompoundSubNavTitleStyle = css({
	[`&:hover, &:focus, &:visited:hover, &:visited:focus`]: {
		color: wacToken('color.primary.blue'),
	},
});
export const CompoundSubNav = ({
	compoundSubNavTitle,
	compoundSubNavLinksProps,
	isShowCta,
	ctaButtonText,
	ctaButtonVariant,
	ctaUrl,
	productKey = 'home',
	optionalClassName = '',
	compoundSubNavTitleHref = '',
}: CompoundSubNavProps) => {
	const compoundSubNavStyle = css({
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: wacToken('color.neutral.light0'),
		height: GLOBAL_NAV_HEIGHT,
		paddingInline: compoundSubNavPadding,
		[media.above.md]: {
			paddingInline: '0',
			marginInline: '60px',
		},
		['&.collections-nav']: {
			[media.above.md]: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				[`.glob-nav-links-container-ul`]: {
					marginInlineStart: token('space.200'),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
					[`.glob-nav-anchor-link`]: {
						paddingInlineStart: token('space.200'),
						paddingInlineEnd: token('space.200'),
						[`&.isCurrentPage`]: {
							borderRadius: 0,
							backgroundImage: `linear-gradient(to top, ${wacToken(
								'color.primary.blue',
							)} 2px, transparent 2px, transparent 100%)`,
						},
					},
				},
			},
		},
	});

	const compoundSubNavBarStyle = css({
		alignItems: 'center',
		justifyContent: 'space-between',
		display: 'flex',
		flex: 1,
		flexDirection: 'row',
		position: 'relative',
		width: '100%',
		maxWidth: '1152px',
		paddingInline: token('space.100'),
		[media.above.md]: {
			paddingInline: '0',
		},
	});

	const compoundSubNavTitleStyle = css({
		textDecoration: 'none',
		fontWeight: '700',
		fontSize: '20px',
		lineHeight: '20px',
		paddingInlineStart: token('space.100'),
		[media.above.md]: {
			paddingInlineStart: '0',
		},
	});

	return (
		<nav className={`compoundSubNav ${optionalClassName}`} css={compoundSubNavStyle}>
			<div className="compoundSubNavBar" css={compoundSubNavBarStyle}>
				<div className="compoundSubNavTitle" css={compoundSubNavTitleStyle}>
					{compoundSubNavTitleHref && (
						<a href={compoundSubNavTitleHref} css={linkableCompoundSubNavTitleStyle}>
							{compoundSubNavTitle}
						</a>
					)}
					{compoundSubNavTitleHref.length === 0 && compoundSubNavTitle}
				</div>
				<CompoundSubNavLinksAndCta
					compoundSubNavLinksProps={compoundSubNavLinksProps}
					isShowCta={isShowCta}
					ctaButtonText={ctaButtonText}
					ctaButtonVariant={ctaButtonVariant}
					ctaUrl={ctaUrl}
					productKey={productKey}
				/>
			</div>
		</nav>
	);
};

CompoundSubNav.displayName = 'CompoundSubNav';

export type CompoundSubNavLinksAndCtaProps = {
	compoundSubNavLinksProps: GlobalNavLinkContentProps[];
	isShowCta?: boolean;
	ctaButtonText?: string;
	ctaButtonVariant?: ButtonVariant;
	ctaUrl?: string;
	productKey?: ProductKeyTypes;
};

const CompoundSubNavLinksAndCta = ({
	compoundSubNavLinksProps,
	isShowCta = false,
	ctaButtonText,
	ctaButtonVariant = 'primary',
	ctaUrl = '#',
	productKey = 'home',
}: CompoundSubNavLinksAndCtaProps) => {
	const ctaButtonDefaultText = useIntl().formatMessage({
		id: 'compoundSubNav.defaultCta.contactSales',
		defaultMessage: 'Contact Sales',
	});
	ctaButtonText = ctaButtonText || ctaButtonDefaultText;
	const ctaButtonUrl = useLocalizedHref(ctaUrl);
	const buttonProps: ButtonProps = {
		variant: ctaButtonVariant,
		href: ctaButtonUrl,
		size: 'small',
		dataEventProps: {
			'data-product-key': productKey,
			'data-event': 'clicked',
			'data-event-component': 'linkButton',
			'data-event-container': 'navbar',
		},
	};

	const linksAndCtaContainerStyle = css({
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		height: '100%',
		position: 'relative',
		alignItems: 'center',
		justifyContent: 'flex-end',
		[media.above.md]: {
			justifyContent: 'space-between',
		},
	});

	const linksContainerStyle = css({
		height: '100%',
		paddingInlineStart: token('space.100'),
		display: 'none',
		[media.above.md]: {
			display: 'block',
		},
	});

	const ctaStyle = css({
		display: 'none',
		[media.above.sm]: {
			display: 'block',
		},
	});

	return (
		<div className="compoundSubNavLinksAndCtaContainer" css={linksAndCtaContainerStyle}>
			<div className="compoundSubNavLinks" css={linksContainerStyle}>
				<GlobalNavLinksContainer globalLinksProps={compoundSubNavLinksProps} isSubnav={true} />
			</div>
			{isShowCta && (
				<div className="compoundSubNavCta" css={ctaStyle}>
					<Button {...buttonProps}>{ctaButtonText}</Button>
				</div>
			)}
			<CompoundSubNavDropdownLinksAndCta
				{...{
					compoundSubNavLinksProps,
					isShowCta,
					ctaButtonText,
					ctaButtonVariant,
					ctaUrl,
				}}
			/>
		</div>
	);
};

const CompoundSubNavDropdownLinksAndCta = ({
	compoundSubNavLinksProps,
	isShowCta,
	ctaButtonText,
	ctaButtonVariant,
	ctaUrl,
}: CompoundSubNavLinksAndCtaProps) => {
	const dropdownContentStyle = css({
		display: 'block',
		paddingInlineStart: token('space.400'),
		paddingInlineEnd: token('space.050'),
		[media.above.sm]: {
			paddingInlineEnd: token('space.200'),
		},
		[media.above.md]: {
			display: 'none',
			padding: 0,
		},
	});

	const [isOpen, setIsOpen] = useState(false);
	const [onCaretButton, setOnCaretButton] = useState(false);
	const [activeSubnavKey, setActiveSubnavKey] = useState<string | null>(null);
	const intl = useIntl();
	const menuId = useId();

	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const backButtonRef = useRef<HTMLButtonElement | null>(null);
	const activeSubnav = compoundSubNavLinksProps.find(
		(link) => link.globalLinksKey === activeSubnavKey,
	);

	const handleEscKeyEvent = useCallback((event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			setIsOpen(false);
			setActiveSubnavKey(null);
		}
	}, []);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (isOpen && !dropdownRef.current?.contains(event.target as Node) && !onCaretButton) {
				setIsOpen(false);
				setActiveSubnavKey(null);
			}
		},
		[isOpen, onCaretButton],
	);

	useEffect(() => {
		if (activeSubnavKey) {
			backButtonRef.current?.focus();
		}
	}, [activeSubnavKey]);

	useEffect(() => {
		// Attach the listeners on component mount.
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscKeyEvent, false);
		// Detach the listeners on component unmount.
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscKeyEvent, false);
		};
	}, [handleEscKeyEvent, handleClickOutside]);

	const linkClickAction: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		setIsOpen((prevIsOpen) => {
			if (prevIsOpen) {
				setActiveSubnavKey(null);
			}
			return !prevIsOpen;
		});
	};

	const rotate180deg = css({
		transform: 'rotate(180deg)',
	});

	const fadeOutKeyframes = keyframes({
		from: { opacity: 1, display: 'block' },
		to: {
			opacity: 0,
			display: 'none',
		},
	});
	const fadeOutAnimation = css({
		animation: `0.25s ${fadeOutKeyframes} linear forwards`,
	});

	const tabletDropdownGap = token('space.100');

	const tabletDropdownHeight = `calc(100vh - 68px - 52.5px - (2 * ${tabletDropdownGap}))`;
	// 68px is the height of the compoundSubNav, 52.5px is the height of the tablet nav, the gaps account for the space between the nav and top of the dropdown, and bottom of the dropdown and screen bottom

	const dropdownContainerStyle = css({
		display: 'none',
		opacity: 1,
		paddingBlock: token('space.200'),
		paddingInline: token('space.300'),
		insetInlineEnd: `calc(${tabletDropdownGap} - ${compoundSubNavPadding})`,
		insetBlockStart: '100%',
		width: 'calc(100% + (2 * ' + compoundSubNavPadding + '))',
		height: `calc(100vh - 68px - 68px)`, // 68px is the height of the compoundSubNav, 68px is the height of the mobile nav
		position: 'absolute',
		order: 100,
		backgroundColor: wacToken('color.neutral.light0'),
		transition: 'width 200ms ease',
		zIndex: 10,
		gap: token('space.150'),
		[media.above.sm]: {
			minWidth: '264px',
			width: 'max-content',
			marginBlockStart: `${tabletDropdownGap}`,
			height: `${tabletDropdownHeight}`,
			borderRadius: '15px',
			border: 'none',
			boxShadow: token('elevation.shadow.overlay'),
		},
	});

	const mobileCtaButtonStyle = css({
		display: 'flex',
		width: '100%',
		[media.above.sm]: {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ctaButton': {
			width: '100%',
			marginInline: 'auto',
		},
	});

	const localizedCtaUrl = useLocalizedHref(ctaUrl as string);

	return (
		<div className="compoundSubNavDropdownLinks" css={dropdownContentStyle}>
			<button
				css={css({
					backgroundColor: 'transparent',
					border: 'none',
				})}
				onClick={linkClickAction}
				// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
				onMouseEnter={() => {
					setOnCaretButton(true);
				}}
				// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
				onMouseLeave={() => {
					setOnCaretButton(false);
				}}
				aria-label={intl.formatMessage({
					id: 'compoundSubNav.openDropdown',
					defaultMessage: 'Open navigation menu',
				})}
				aria-expanded={isOpen}
				aria-controls={menuId}
				data-event="clicked"
				data-event-component="linkButton"
				data-event-container="headerDropdownLink"
				data-testid="compoundSubNavDropdownLinksCaretButton"
			>
				<div
					className="chevron-icon"
					css={[
						css({
							display: 'block',
							lineHeight: 0,
							transition: '250ms',
						}),
						isOpen && rotate180deg,
					]}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="15"
						height="10"
						viewBox="0 0 15 10"
						fill="none"
						aria-hidden="true"
						focusable="false"
					>
						<path
							d="M0.571934 1.12105C0.441394 1.25145 0.337835 1.40629 0.267179 1.57674C0.196524 1.74718 0.160156 1.92988 0.160156 2.11439C0.160156 2.2989 0.196524 2.48159 0.267179 2.65204C0.337835 2.82248 0.441394 2.97733 0.571934 3.10772L6.6346 9.18372C6.75846 9.30789 6.90559 9.4064 7.06758 9.47362C7.22956 9.54084 7.40322 9.57544 7.5786 9.57544C7.75398 9.57544 7.92764 9.54084 8.08962 9.47362C8.25161 9.4064 8.39874 9.30789 8.5226 9.18372L14.5893 3.10639C14.8524 2.84329 15.0002 2.48646 15.0002 2.11439C15.0002 1.74232 14.8524 1.38548 14.5893 1.12239C14.3262 0.859292 13.9693 0.711487 13.5973 0.711487C13.2252 0.711487 12.8684 0.859292 12.6053 1.12239L7.5786 6.15705L2.5546 1.12239C2.42446 0.992129 2.26992 0.888796 2.09982 0.818295C1.92972 0.747793 1.7474 0.711505 1.56327 0.711505C1.37914 0.711505 1.19681 0.747793 1.02671 0.818295C0.856613 0.888796 0.702076 0.992129 0.571934 1.12239V1.12105Z"
							fill={wacToken('color.primary.black')}
						/>
					</svg>
				</div>
			</button>
			<div
				id={menuId}
				css={[
					dropdownContainerStyle,
					isOpen && css({ display: 'flex', flexDirection: 'column' }),
					!isOpen && fadeOutAnimation,
				]}
				ref={dropdownRef}
				aria-hidden={!isOpen}
			>
				<div
					css={css({
						display: 'none',
						[media.only.sm]: {
							display: 'block',
							position: 'fixed',
							insetBlockStart: `calc(100vh - ${tabletDropdownHeight} - ${tabletDropdownGap})`,
							insetInlineStart: 0,
							width: '100vw',
							height: '100%',
							pointerEvents: 'none',
							// eslint-disable-next-line local/ensure-wac-token-usage
							background: 'rgba(255, 255, 255, .5)',
							transition: 'opacity .25s',
							zIndex: -1,
						},
					})}
				/>
				{isShowCta && (
					<div css={mobileCtaButtonStyle}>
						<Button
							variant={ctaButtonVariant}
							href={ctaUrl ? localizedCtaUrl : ctaUrl}
							size="small"
							className="ctaButton"
						>
							{ctaButtonText}
						</Button>
					</div>
				)}
				{activeSubnav ? (
					<div>
						<button
							ref={backButtonRef}
							type="button"
							css={css({
								display: 'flex',
								alignItems: 'center',
								gap: token('space.100'),
								border: 'none',
								backgroundColor: 'transparent',
								color: wacToken('color.primary.blue'),
								padding: token('space.100'),
								['&:focus-visible']: {
									outline: `2px solid ${wacToken('color.primary.blue')}`,
									outlineOffset: '2px',
								},
							})}
							onClick={() => setActiveSubnavKey(null)}
						>
							<ChevronLeftIcon label="" />
							{intl.formatMessage({
								id: 'globalnavv2.menu-back',
								defaultMessage: 'Back',
							})}
						</button>
						<div>{activeSubnav.content}</div>
					</div>
				) : (
					<div>
						{compoundSubNavLinksProps.map((link) => (
							<div
								key={link.globalLinksKey}
								css={css({
									paddingBlock: token('space.200'),
									width: '100%',
								})}
							>
								{link.hasSubnav
									? CompoundNavDropdownLinkWithSubnav({
											...link,
											onOpen: () => setActiveSubnavKey(link.globalLinksKey),
										})
									: CompoundNavDropdownLink(link as DropdownLinkProps)}
							</div>
						))}
					</div>
				)}
				<div
					css={css({
						display: 'block',
						[media.above.sm]: { display: 'none' },
					})}
				>
					<Divider />
				</div>
			</div>
		</div>
	);
};

const dropdownLinkStyle = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	fontWeight: 700,
	color: wacToken('color.primary.black'),
	lineHeight: '20px',
	height: '20px',
	'&:hover': {
		color: wacToken('color.primary.blue'),
	},
});

type DropdownLinkProps = {
	linkText: string;
	href: string;
};

const CompoundNavDropdownLink = (link: DropdownLinkProps) => {
	return (
		<a
			href={useLocalizedHref(link.href)}
			css={[
				dropdownLinkStyle,
				css({
					display: 'inline-block',
					width: '100%',
				}),
			]}
		>
			{link.linkText}
		</a>
	);
};

type DropdownLinkWithSubNavProps = {
	linkText: string;
	onOpen: () => void;
};

const CompoundNavDropdownLinkWithSubnav = ({
	linkText,
	onOpen,
}: //isActive = false,
//content,
//layout
DropdownLinkWithSubNavProps) => {
	const subNavDropdownLinkStyle = css({
		cursor: 'pointer',
		display: 'flex',
		width: '100%',
		justifyContent: 'space-between',
		alignItems: 'center',
		height: '20px',
		lineHeight: '20px',
		border: 'none',
		backgroundColor: 'transparent',
		padding: 0,
	});

	const subNavCaretInDropdown = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="6"
			height="10"
			viewBox="0 0 6 10"
			fill="none"
			aria-hidden="true"
			focusable="false"
		>
			<path
				d="M1.36328 1L4.99964 5L1.36328 9"
				stroke={wacToken('color.primary.black')}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);

	return (
		<button type="button" onClick={onOpen} css={subNavDropdownLinkStyle}>
			<span css={dropdownLinkStyle}>{linkText}</span>
			{subNavCaretInDropdown}
		</button>
	);
};
